import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Youtube, Loader2, Send, Sparkles, List, Film, SaveAll, Check, FileDown, FileText, ClipboardPaste, AlertTriangle, FolderOpen, Bot, GitCompareArrows, Mic, Quote, ListVideo } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { ThemeToggle } from "@/components/ThemeToggle";
import { AutoSaveIndicator } from "@/components/AutoSaveIndicator";
import { supabase } from "@/integrations/supabase/client";
import { VideoResultCard } from "@/components/content-summary/VideoResultCard";
import { CompareMode } from "@/components/content-summary/CompareMode";
import { DeepDiveMode } from "@/components/content-summary/DeepDiveMode";
import { KeyQuotesMode } from "@/components/content-summary/KeyQuotesMode";
import { PlaylistMode } from "@/components/content-summary/PlaylistMode";
import { extractVideoId, streamSummary } from "@/components/content-summary/utils";
import { autoSaveSummary } from "@/components/content-summary/autoSave";
import type { VideoResult } from "@/components/content-summary/types";

export default function ContentSummary() {
  const [mode, setMode] = useState<"single" | "batch" | "compare" | "deepdive" | "quotes" | "playlist">("single");
  const [singleUrl, setSingleUrl] = useState("");
  const [selectedModel, setSelectedModel] = useState("auto");
  const [batchUrls, setBatchUrls] = useState("");
  const [results, setResults] = useState<VideoResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [savingAll, setSavingAll] = useState(false);
  const [filterLimited, setFilterLimited] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const abortRef = useRef<AbortController | null>(null);

  // Load a specific summary if restore_id is in URL params
  useEffect(() => {
    const restoreId = searchParams.get("restore");
    if (restoreId && user?.id) {
      loadSavedById(restoreId);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, user]);

  const loadSavedById = async (id: string) => {
    setLoadingSaved(true);
    const { data, error } = await supabase
      .from("content_summaries")
      .select("*")
      .eq("id", id)
      .single();
    setLoadingSaved(false);
    if (error || !data) {
      toast({ title: "Load failed", description: error?.message || "Summary not found.", variant: "destructive" });
      return;
    }
    const limitedInfo = /captions were unavailable|no captions available|no captions or description|based on.*title alone/i.test(data.summary_content);
    setResults([{
      id: data.id,
      url: data.video_url,
      videoId: data.video_id,
      summary: data.summary_content,
      status: "done",
      saved: true,
      limitedInfo,
      author: (data as any).author || undefined,
    }]);
  };

  const loadAllSaved = async () => {
    if (!user?.id) {
      toast({ title: "Sign in required", description: "Please sign in to load saved summaries.", variant: "destructive" });
      return;
    }
    setLoadingSaved(true);
    const { data, error } = await supabase
      .from("content_summaries")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(50);
    setLoadingSaved(false);
    if (error) {
      toast({ title: "Load failed", description: error.message, variant: "destructive" });
      return;
    }
    if (!data || data.length === 0) {
      toast({ title: "No saved summaries", description: "You haven't saved any summaries yet." });
      return;
    }
    const loaded: VideoResult[] = data.map(d => {
      const limitedInfo = /captions were unavailable|no captions available|no captions or description|based on.*title alone/i.test(d.summary_content);
      return {
        id: d.id,
        url: d.video_url,
        videoId: d.video_id,
        summary: d.summary_content,
        status: "done" as const,
        saved: true,
        limitedInfo,
        author: (d as any).author || undefined,
      };
    });
    setResults(loaded);
    toast({ title: "Loaded", description: `${loaded.length} saved summary(ies) restored.` });
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const updateResult = useCallback((id: string, patch: Partial<VideoResult>) => {
    setResults(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  }, []);

  const handleRetry = async (id: string) => {
    const item = results.find(r => r.id === id);
    if (!item || !item.url || !/(?:youtube\.com\/(?:watch|embed|shorts)|youtu\.be\/)/.test(item.url)) {
      toast({ title: "Cannot retry", description: "No valid YouTube URL associated with this summary.", variant: "destructive" });
      return;
    }
    updateResult(id, { status: "loading", summary: "", limitedInfo: false, saved: false, author: undefined });
    try {
      const ac = new AbortController();
      const result = await streamSummary(
        item.url,
        (text) => updateResult(id, { summary: text }),
        ac.signal,
        (meta) => updateResult(id, { author: meta.author, model: meta.model, language: meta.language }),
        selectedModel,
      );
      const limitedInfo = /captions were unavailable|no captions available|no captions or description|based on.*title alone/i.test(result.summary);
      updateResult(id, { summary: result.summary, status: "done", limitedInfo, author: result.author, model: result.model, language: result.language });
      // Auto-save on retry
      const didSave = await autoSaveSummary({ userId: user?.id, videoUrl: item.url, videoId: item.videoId, summaryContent: result.summary, author: result.author });
      if (didSave) updateResult(id, { saved: true });
    } catch (error: any) {
      updateResult(id, { status: "error", error: error.message });
    }
  };

  const processUrls = async (urls: string[]) => {
    const items: VideoResult[] = urls.map((u, i) => ({
      id: `${Date.now()}-${i}`,
      url: u,
      videoId: extractVideoId(u),
      summary: "",
      status: "pending" as const,
    }));
    setResults(items);
    setIsProcessing(true);
    abortRef.current = new AbortController();

    for (const item of items) {
      if (abortRef.current.signal.aborted) break;
      setResults(prev => prev.map(r => r.id === item.id ? { ...r, status: "loading" } : r));
      try {
        const result = await streamSummary(
          item.url,
          (text) => setResults(prev => prev.map(r => r.id === item.id ? { ...r, summary: text } : r)),
          abortRef.current.signal,
          (meta) => setResults(prev => prev.map(r => r.id === item.id ? { ...r, author: meta.author, model: meta.model, language: meta.language } : r)),
          selectedModel,
        );
        const limitedInfo = /captions were unavailable|no captions available|no captions or description|based on.*title alone/i.test(result.summary);
        setResults(prev => prev.map(r => r.id === item.id ? { ...r, summary: result.summary, status: "done", limitedInfo, author: result.author, model: result.model, language: result.language } : r));
        // Auto-save
        const didSave = await autoSaveSummary({ userId: user?.id, videoUrl: item.url, videoId: item.videoId, summaryContent: result.summary, author: result.author });
        if (didSave) {
          setResults(prev => prev.map(r => r.id === item.id ? { ...r, saved: true } : r));
        }
      } catch (error: any) {
        if (error.name === "AbortError") break;
        setResults(prev => prev.map(r => r.id === item.id ? { ...r, status: "error", error: error.message } : r));
      }
    }
    setIsProcessing(false);
  };

  const isValidYoutubeUrl = (url: string) =>
    /(?:youtube\.com\/(?:watch|embed|shorts)|youtu\.be\/)/.test(url);

  const handleSubmit = () => {
    if (mode === "single") {
      const url = singleUrl.trim();
      if (!url) return;
      if (!isValidYoutubeUrl(url)) {
        toast({ title: "Invalid URL", description: "Please enter a valid YouTube video URL.", variant: "destructive" });
        return;
      }
      processUrls([url]);
    } else {
      const urls = batchUrls
        .split("\n")
        .map(l => l.trim())
        .filter(l => l.length > 0);
      const valid = urls.filter(isValidYoutubeUrl);
      const invalid = urls.length - valid.length;
      if (valid.length === 0) {
        toast({ title: "No valid URLs", description: "Please enter valid YouTube video URLs.", variant: "destructive" });
        return;
      }
      if (invalid > 0) {
        toast({ title: `${invalid} invalid URL(s) skipped`, description: "Only valid YouTube URLs will be processed." });
      }
      processUrls(valid);
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
    setIsProcessing(false);
  };

  const handleSaveAll = async () => {
    if (!user?.id) {
      toast({ title: "Sign in required", description: "Please sign in to save summaries.", variant: "destructive" });
      return;
    }
    const unsaved = results.filter(r => r.status === "done" && !r.saved);
    if (unsaved.length === 0) return;
    setSavingAll(true);
    const rows = unsaved.map(r => ({
      user_id: user.id,
      video_url: r.url,
      video_id: r.videoId,
      summary_content: r.summary,
      author: r.author || null,
    }));
    const { error } = await supabase.from("content_summaries").insert(rows);
    setSavingAll(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } else {
      setResults(prev => prev.map(r => r.status === "done" ? { ...r, saved: true } : r));
      toast({ title: "All saved", description: `${unsaved.length} summaries saved to Collected Data.` });
    }
  };

  const handleExportMarkdown = () => {
    const done = results.filter(r => r.status === "done");
    if (done.length === 0) return;
    const md = done.map((r, i) => {
      const header = `# Video ${i + 1}: ${r.url}\n`;
      return header + r.summary;
    }).join("\n\n---\n\n");
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `summaries-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Exported", description: "Markdown file downloaded." });
  };

  const handleExportPdf = async () => {
    const done = results.filter(r => r.status === "done");
    if (done.length === 0) return;
    const { default: jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const maxWidth = pageWidth - margin * 2;
    let y = 20;

    const addText = (text: string, fontSize: number, isBold = false) => {
      doc.setFontSize(fontSize);
      doc.setFont("helvetica", isBold ? "bold" : "normal");
      const lines = doc.splitTextToSize(text, maxWidth);
      for (const line of lines) {
        if (y > 275) { doc.addPage(); y = 20; }
        doc.text(line, margin, y);
        y += fontSize * 0.45;
      }
      y += 3;
    };

    done.forEach((r, i) => {
      if (i > 0) { doc.addPage(); y = 20; }
      addText(`Video ${i + 1}: ${r.url}`, 14, true);
      y += 4;
      // Simple markdown-to-text: strip markdown syntax
      const plain = r.summary
        .replace(/^#{1,6}\s+/gm, "")
        .replace(/\*\*(.*?)\*\*/g, "$1")
        .replace(/\*(.*?)\*/g, "$1")
        .replace(/^[-*]\s+/gm, "• ")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
      const paragraphs = plain.split(/\n\n+/);
      for (const p of paragraphs) {
        const trimmed = p.trim();
        if (!trimmed) continue;
        addText(trimmed, 10);
      }
    });

    doc.save(`summaries-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast({ title: "Exported", description: "PDF file downloaded." });
  };

  const completedCount = results.filter(r => r.status === "done").length;
  const unsavedDoneCount = results.filter(r => r.status === "done" && !r.saved).length;
  const limitedCount = results.filter(r => r.limitedInfo).length;
  const totalCount = results.length;
  const filteredResults = filterLimited ? results.filter(r => r.limitedInfo) : results;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-red-500/10 rounded-lg">
                <Sparkles className="h-5 w-5 text-red-500" />
              </div>
              <h1 className="text-xl font-bold text-foreground">Content Summary</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadAllSaved}
              disabled={loadingSaved || isProcessing}
            >
              {loadingSaved ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FolderOpen className="h-4 w-4 mr-1" />}
              Load Saved
            </Button>
            <AutoSaveIndicator />
            <ThemeToggle />
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6">
        {/* Mode Tabs */}
        <div className="flex justify-end">
         <Tabs value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
            <TabsList className="h-8 flex-wrap">
              <TabsTrigger value="single" className="gap-1.5 text-xs px-3 h-7">
                <Film className="h-3.5 w-3.5" /> Single
              </TabsTrigger>
              <TabsTrigger value="batch" className="gap-1.5 text-xs px-3 h-7">
                <List className="h-3.5 w-3.5" /> Batch
              </TabsTrigger>
              <TabsTrigger value="playlist" className="gap-1.5 text-xs px-3 h-7">
                <ListVideo className="h-3.5 w-3.5" /> Playlist
              </TabsTrigger>
              <TabsTrigger value="compare" className="gap-1.5 text-xs px-3 h-7">
                <GitCompareArrows className="h-3.5 w-3.5" /> Compare
              </TabsTrigger>
              <TabsTrigger value="quotes" className="gap-1.5 text-xs px-3 h-7">
                <Quote className="h-3.5 w-3.5" /> Quotes
              </TabsTrigger>
              <TabsTrigger value="deepdive" className="gap-1.5 text-xs px-3 h-7">
                <Mic className="h-3.5 w-3.5" /> Deep Dive
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {mode === "compare" ? (
          <CompareMode userId={user?.id} />
        ) : mode === "deepdive" ? (
          <DeepDiveMode userId={user?.id} />
        ) : mode === "quotes" ? (
          <KeyQuotesMode userId={user?.id} />
        ) : mode === "playlist" ? (
          <PlaylistMode userId={user?.id} />
        ) : (
        <>
        {/* Input Card */}
        <Card className="border-red-500/10 bg-card/90 backdrop-blur-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
                <Youtube className="h-5 w-5 text-red-500" />
                <CardTitle className="text-lg">YouTube Video Summarizer</CardTitle>
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardDescription className="flex-1">
                {mode === "single"
                  ? "Paste a YouTube URL to get a detailed AI summary."
                  : "Paste multiple YouTube URLs (one per line) to summarize them all sequentially."}
              </CardDescription>
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-muted-foreground" />
                <Select value={selectedModel} onValueChange={setSelectedModel} disabled={isProcessing}>
                  <SelectTrigger className="h-8 w-[180px] text-xs">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">🤖 Auto (Smart Select)</SelectItem>
                    <SelectItem value="gemini-3.1-pro">🏆 Gemini 3.1 Pro</SelectItem>
                    <SelectItem value="gemini-2.5-pro">⚡ Gemini 2.5 Pro</SelectItem>
                    <SelectItem value="gemini-3-flash">🚀 Gemini 3 Flash</SelectItem>
                    <SelectItem value="gemini-2.5-flash">💡 Gemini 2.5 Flash</SelectItem>
                    <SelectItem value="gemini-2.5-flash-lite">🪶 Gemini 2.5 Flash Lite</SelectItem>
                    <SelectItem value="gpt-5.2">🧠 GPT-5.2</SelectItem>
                    <SelectItem value="gpt-5">🎯 GPT-5</SelectItem>
                    <SelectItem value="gpt-5-mini">💨 GPT-5 Mini</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
              className="space-y-3"
            >
              {mode === "single" ? (
                <div className="flex gap-2">
                  <Input
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={singleUrl}
                    onChange={(e) => setSingleUrl(e.target.value)}
                    disabled={isProcessing}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={isProcessing}
                    title="Paste from clipboard"
                    onClick={async () => {
                      try {
                        const text = await navigator.clipboard.readText();
                        if (text.trim()) {
                          setSingleUrl(text.trim());
                          toast({ title: "Pasted", description: "URL pasted from clipboard." });
                        }
                      } catch {
                        toast({ title: "Clipboard access denied", description: "Please allow clipboard access or paste manually.", variant: "destructive" });
                      }
                    }}
                  >
                    <ClipboardPaste className="h-4 w-4" />
                  </Button>
                  <Button
                    type="submit"
                    disabled={isProcessing || !singleUrl.trim()}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    <span className="ml-2 hidden sm:inline">{isProcessing ? "Analyzing…" : "Summarize"}</span>
                  </Button>
                </div>
              ) : (
                <>
                  <Textarea
                    placeholder={"https://www.youtube.com/watch?v=abc123\nhttps://www.youtube.com/watch?v=def456\nhttps://youtu.be/ghi789"}
                    value={batchUrls}
                    onChange={(e) => setBatchUrls(e.target.value)}
                    disabled={isProcessing}
                    rows={5}
                    className="font-mono text-sm"
                  />
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      {batchUrls.split("\n").filter(l => l.trim()).length} URL(s) detected
                    </p>
                    <div className="flex gap-2">
                      {isProcessing && (
                        <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
                          Cancel
                        </Button>
                      )}
                      <Button
                        type="submit"
                        disabled={isProcessing || !batchUrls.trim()}
                        className="bg-red-500 hover:bg-red-600 text-white"
                      >
                        {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                        {isProcessing ? "Processing…" : "Summarize All"}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Progress bar for batch */}
        {results.length > 1 && (
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }}
              />
            </div>
            <Badge variant="outline" className="shrink-0">
              {completedCount}/{totalCount} done
            </Badge>
            {!isProcessing && unsavedDoneCount > 0 && (
              <Button
                size="sm"
                onClick={handleSaveAll}
                disabled={savingAll}
                className="shrink-0"
              >
                {savingAll ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : unsavedDoneCount === 0 ? <Check className="h-4 w-4 mr-1" /> : <SaveAll className="h-4 w-4 mr-1" />}
                {savingAll ? "Saving…" : `Save All (${unsavedDoneCount})`}
              </Button>
            )}
            {!isProcessing && completedCount > 0 && (
              <>
                <Button size="sm" variant="outline" onClick={handleExportMarkdown} className="shrink-0">
                  <FileText className="h-4 w-4 mr-1" /> .md
                </Button>
                <Button size="sm" variant="outline" onClick={handleExportPdf} className="shrink-0">
                  <FileDown className="h-4 w-4 mr-1" /> .pdf
                </Button>
              </>
            )}
            {!isProcessing && limitedCount > 0 && (
              <Button
                size="sm"
                variant={filterLimited ? "default" : "outline"}
                onClick={() => setFilterLimited(f => !f)}
                className="shrink-0"
              >
                <AlertTriangle className="h-4 w-4 mr-1" />
                {filterLimited ? `Showing ${limitedCount} limited` : `${limitedCount} limited`}
              </Button>
            )}
          </div>
        )}

        {/* Results */}
        {filteredResults.length > 0 && (
          <div className="space-y-4">
            {filteredResults.map((r) => (
              <VideoResultCard
                key={r.id}
                result={r}
                userId={user?.id}
                onSaved={(id) => updateResult(id, { saved: true })}
                onRetry={handleRetry}
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {results.length === 0 && !isProcessing && (
          <div className="text-center py-16">
            <div className="inline-block p-6 bg-muted/50 rounded-full mb-4">
              <Youtube className="h-12 w-12 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Ready to Analyze</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Paste one or multiple YouTube video URLs to get comprehensive, AI-powered summaries with key points, detailed analysis, and actionable takeaways.
            </p>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
